import classNames from 'classnames';
import PropTypes from 'prop-types';
import * as React from 'react';
import MiniUser from '../../../../types/MiniUser';
import { UserProfileDto } from '../../../../types/UserDtos';
import { AppContext, withAppContext } from '../../hoc/withAppContext';
import { getCurrentUser } from '../../services/CurrentUser';
import { isFeatureEnabled } from '../../services/Features';
import { getDisplayedName, getLink } from '../../services/ProfileInfo';
import { CategoryNames } from '../../services/Tracking';
import { isAnonymous, isDeleted, isTaskUser } from '../../services/User';
import Avatar from '../Avatar';
import { AvatarSize } from '../Avatar/service';
import Link from '../Link';
import styles from './style.m.less';

type Props = {
    user: MiniUser | UserProfileDto;
    appContext: AppContext;
    isInline?: boolean;
    noLink?: boolean;
    customLink?: string;
    hiddenLink?: boolean;
    noAvatar?: boolean;
    avatarSize?: AvatarSize;
    ellipsize?: boolean;
    openInNewTab?: boolean;
    useFirstName?: boolean;
    className?: string;
    eventTrackingCategory?: CategoryNames;
};

export const toUserKey = (user: MiniUser, i: number) => {
    if (isAnonymous(user)) {
        return `${user.id}-${i}`;
    }
    return user.id;
};

const Wrapper: React.FC<
    React.PropsWithChildren<{
        profileUrl?: string | null;
        className?: string;
        openInNewTab: boolean;
        userId: string;
        eventTrackingCategory?: CategoryNames;
    }>
> = ({ profileUrl, className, openInNewTab, children, userId, eventTrackingCategory }) =>
    profileUrl ? (
        <Link
            href={profileUrl}
            className={className}
            openInNewTab={openInNewTab}
            eventTracking={
                eventTrackingCategory
                    ? { category: eventTrackingCategory, action: 'navigate to profile', label: userId }
                    : undefined
            }
        >
            {children}
        </Link>
    ) : (
        <span className={className}>{children}</span>
    );

const MiniProfile: React.FC<Props> = ({
    user,
    appContext,
    isInline = false,
    className,
    noLink = false,
    hiddenLink = false,
    avatarSize = 'XS',
    noAvatar = false,
    ellipsize = false,
    useFirstName = false,
    openInNewTab = false,
    eventTrackingCategory,
    customLink,
}) => {
    noLink =
        noLink ||
        isAnonymous(user) ||
        isDeleted(user) ||
        isTaskUser(user) ||
        // MiniProfile can be rendered outside SPA context (Praise Wall), currentUser might not be available
        (getCurrentUser()?.isGuest && !isFeatureEnabled('FEATURE_guestUserProfileAccess'));
    ellipsize = ellipsize && !isInline;
    hiddenLink = hiddenLink && !noLink;

    const userName = getDisplayedName(appContext.user, user, useFirstName);
    const profileUrl = noLink ? null : customLink || getLink(user);

    const userNameClass = classNames({
        [styles.ellipsize]: ellipsize,
    });

    const wrapperClass = classNames(styles.link, className, {
        [styles.inline]: isInline,
        [styles.deactivated]: !user.isActive,
        [styles.ellipsizeWrapper]: ellipsize,
        [styles.hiddenLink]: hiddenLink,
    });

    return (
        <Wrapper
            profileUrl={profileUrl}
            className={wrapperClass}
            openInNewTab={openInNewTab}
            userId={user.id}
            eventTrackingCategory={eventTrackingCategory}
        >
            {!noAvatar &&
                !!user.logo &&
                (isInline ? (
                    <span className={styles.inlineAvatarWrapper}>
                        <Avatar user={user} size={avatarSize} ariaHidden />
                    </span>
                ) : (
                    <Avatar user={user} size={avatarSize} ariaHidden />
                ))}
            <span className={userNameClass}>{userName}</span>
        </Wrapper>
    );
};

MiniProfile.propTypes = {
    isInline: PropTypes.bool,
    noLink: PropTypes.bool,
    customLink: PropTypes.string,
    noAvatar: PropTypes.bool,
    ellipsize: ((...args: [Props, keyof Props, string, string, string]) => {
        const [props, propName, componentName] = args;
        if (props.isInline && props[propName]) {
            return new Error(`${componentName}: using isInline and ${propName} together is not supported`);
        } else {
            return PropTypes.bool(...args);
        }
    }) as PropTypes.Validator<any>,
    openInNewTab: PropTypes.bool,
    useFirstName: PropTypes.bool,
    className: PropTypes.string,
    eventTrackingCategory: PropTypes.any,
};

export default withAppContext(MiniProfile);
